import { createElement, useEffect, useRef, useState } from "react";

/**
 * Renders a real email template inside a miniature scaled iframe.
 * Uses IntersectionObserver to only render when the card is visible.
 */

// Same dynamic import map as useTemplatePreview
const templateImports: Record<string, () => Promise<{ default?: React.ComponentType; [key: string]: unknown }>> = {};

const modules = import.meta.glob<{ default?: React.ComponentType; [key: string]: unknown }>(
  "../../email-templates/**/*.tsx",
  { eager: false }
);

for (const [path, loader] of Object.entries(modules)) {
  const match = path.match(/email-templates\/([^/]+)\/([^/]+)\.tsx$/);
  if (match && !match[1].startsWith("_")) {
    const key = `${match[1]}/${match[2]}`;
    templateImports[key] = loader as () => Promise<{ default?: React.ComponentType; [key: string]: unknown }>;
  }
}

// Cache rendered HTML so we don't re-render the same template
const htmlCache = new Map<string, string>();

async function renderTemplateHtml(brand: string, slug: string): Promise<string> {
  const key = `${brand}/${slug}`;

  // Check cache
  const cached = htmlCache.get(key);
  if (cached) return cached;

  const loader = templateImports[key];
  if (!loader) throw new Error(`No template: ${key}`);

  const mod = await loader();
  const Component =
    mod.default ||
    (Object.values(mod).find((v) => typeof v === "function") as React.ComponentType | undefined);

  if (!Component) throw new Error(`No component in ${key}`);

  const { render } = await import("@react-email/render");
  const html = await render(createElement(Component as React.ComponentType));

  htmlCache.set(key, html);
  return html;
}

interface TemplateCardPreviewProps {
  brand: string;
  slug: string;
  accent: string;
}

export function TemplateCardPreview({ brand, slug, accent }: TemplateCardPreviewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // IntersectionObserver — only render when card scrolls into view
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Once visible, stay loaded
        }
      },
      { rootMargin: "200px" } // Start loading 200px before entering viewport
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Render template HTML when visible
  useEffect(() => {
    if (!isVisible) return;

    let cancelled = false;
    renderTemplateHtml(brand, slug)
      .then((h) => {
        if (!cancelled) setHtml(h);
      })
      .catch(() => {
        if (!cancelled) setHtml(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isVisible, brand, slug]);

  // The preview is rendered in a 580px-wide iframe, then scaled down to fit the card
  const previewWidth = 580;
  const previewHeight = 520;
  const scale = 0.48;
  const containerWidth = previewWidth * scale;
  const containerHeight = previewHeight * scale;

  return (
    <div
      ref={ref}
      className="relative overflow-hidden rounded-md bg-slate-50"
      style={{ width: containerWidth, height: containerHeight, margin: "0 auto" }}
    >
      {html ? (
        <iframe
          srcDoc={html}
          title={`${brand}/${slug} preview`}
          style={{
            width: previewWidth,
            height: previewHeight,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            border: "none",
            pointerEvents: "none",
          }}
          sandbox="allow-same-origin"
        />
      ) : (
        // Loading skeleton
        <div
          className="flex items-center justify-center"
          style={{ width: containerWidth, height: containerHeight }}
        >
          <div className="space-y-2 w-4/5">
            <div className="h-2 rounded-full bg-slate-200" />
            <div className="h-2 w-3/4 rounded-full bg-slate-100" />
            <div className="h-2 w-1/2 rounded-full bg-slate-100" />
            <div className="mt-3 h-8 rounded-md" style={{ backgroundColor: accent }} />
          </div>
        </div>
      )}
    </div>
  );
}